import { HttpErrorResponse, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as Sentry from '@sentry/angular';

/** Max characters of a response body kept in the Sentry event. */
const MAX_BODY_CHARS = 2000;

/**
 * Turns a failed HTTP response into a Sentry issue.
 *
 * Deciding *whether* a failure is worth reporting is not this service's job:
 * `ApiInterceptor` calls it only after every response it deliberately handles
 * has already returned, so the interceptor's control flow is the single
 * definition of "unexpected". Duplicating that list here would only give it a
 * second copy to drift out of sync with.
 *
 * This exists because nothing else reports failed requests: the Sentry browser
 * SDK turns them into breadcrumbs and `http.client` spans but never into
 * issues, and the only other route -- `GlobalErrorHandler` -- fires solely for
 * *unhandled* errors, so any request whose caller catches the error (all of the
 * paginated services, for instance) used to be invisible.
 */
@Injectable({
    providedIn: 'root',
})
export class HttpErrorReporterService {
    /**
     * The only unconditional filter, needed because the interceptor's
     * self-service and post-401 retry paths report without going through that
     * control flow.
     *
     * 404 is deliberately absent: a mistyped or malformed URL is exactly the
     * bug class this reporter exists to catch. Add 404 here if routine
     * "resource is gone" 404s turn out to be noisy.
     */
    private static readonly IGNORED_STATUSES = new Set([
        // Aborted, offline or CORS-blocked -- no response ever arrived, so
        // there is nothing actionable and plenty of noise on flaky connections.
        0,
        // Logged out -- the interceptor retries once with a fresh token and
        // then redirects to /login.
        401,
    ]);

    /** Reports a failed HTTP response to Sentry as a handled exception. */
    report(request: HttpRequest<unknown>, error: unknown): void {
        if (!(error instanceof HttpErrorResponse)) {
            return;
        }
        if (HttpErrorReporterService.IGNORED_STATUSES.has(error.status)) {
            return;
        }

        const url = error.url ?? request.url;
        const path = this.normalizePath(url);

        Sentry.captureException(
            new Error(`HTTP ${error.status} ${request.method} ${path}`),
            {
                mechanism: { type: 'angular.http', handled: true },
                captureContext: {
                    // Group by route template + method + status rather than by
                    // the full URL, so every paging/sorting variant of one
                    // broken endpoint stays a single issue.
                    fingerprint: [
                        'http',
                        request.method,
                        path,
                        String(error.status),
                    ],
                    tags: {
                        'http.status': error.status,
                        'http.method': request.method,
                        'http.path': path,
                    },
                    extra: {
                        url,
                        statusText: error.statusText,
                        // Correlates the event with the backend log entry.
                        requestId: error.headers?.get('x-request-id') ?? null,
                        responseBody: this.describeBody(error.error),
                    },
                },
            }
        );
    }

    /**
     * Reduces a URL to a groupable route template: drops origin and query
     * string and replaces id-ish segments, so that
     * `/organization-sites/216477975/...` and `/organization-sites/216477976/...`
     * land in the same Sentry issue.
     */
    private normalizePath(url: string): string {
        const path = url.split('?')[0].replace(/^[a-z]+:\/\/[^/]+/i, '');
        return path
            .split('/')
            .map(segment => (this.isIdSegment(segment) ? ':id' : segment))
            .join('/');
    }

    private isIdSegment(segment: string): boolean {
        if (/^\d+$/.test(segment)) {
            return true;
        }
        if (
            /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
                segment
            )
        ) {
            return true;
        }
        // Opaque API keys: long, digit-bearing, no separators other than -/_.
        // Route names such as `delivery-notes` carry no digit and are kept.
        return (
            segment.length >= 16 &&
            /\d/.test(segment) &&
            /^[A-Za-z0-9_-]+$/.test(segment)
        );
    }

    /**
     * Response bodies reach us as strings, as parsed JSON, or -- for `blob` and
     * `arraybuffer` response types -- as binary that would serialize to a
     * useless `{}`. Keep something readable and bounded either way.
     */
    private describeBody(body: unknown): unknown {
        if (body === null || body === undefined) {
            return null;
        }
        if (typeof body === 'string') {
            return body.slice(0, MAX_BODY_CHARS);
        }
        if (body instanceof Blob) {
            return `[Blob ${body.size} bytes, ${body.type || 'unknown type'}]`;
        }
        if (body instanceof ArrayBuffer) {
            return `[ArrayBuffer ${body.byteLength} bytes]`;
        }
        try {
            const json = JSON.stringify(body);
            return json.length > MAX_BODY_CHARS
                ? `${json.slice(0, MAX_BODY_CHARS)}...[truncated]`
                : body;
        } catch {
            return '[unserializable body]';
        }
    }
}
